import { Browser } from '@capacitor/browser';
import { openAndWaitForCallback } from '@tbitop/platform';
import { isNull } from 'lodash-es';
import { ChatCenteredDots, CheckCircle, ClipboardText, XCircle } from 'phosphor-react';
import { ButtonCtaDanger, ButtonCtaGhost, ButtonCtaPrimary, ButtonCtaTertiary } from '../../lib/ds/button/button';
import { ergebnisLabelMapping, ErgebnisOptionenModal, useErgebnisOptionenModal } from './ergebnis-optionen-modal';
import type { AufgabeAbschliessenModel } from './model';
import type { AufgabeErgebnis } from './utils/constants';
import { getErgebnisoptionen } from './utils/helper';


type UmfrageAufgabeAktionenProps = {
  aufgabe: AufgabeAbschliessenModel;
  onAufgabeAbschliessen: (id: string, ergebnis: string, ergebnisOption?: string, kommentar?: string) => Promise<unknown>;
  onAufgabeZwischenergebnisErfassen?: (id: string, zwischenErgebnisOption: string, kommentar: string) => Promise<unknown>;
  canAufgabeAbschliessen: ((aufgabe: { status: string | null; ergebnis: string | null }) => boolean);
};

export const UmfrageAufgabeAktionen = ({ aufgabe, onAufgabeAbschliessen, onAufgabeZwischenergebnisErfassen, canAufgabeAbschliessen }: UmfrageAufgabeAktionenProps) => {
  const [erfolgreichErgebnisOption] = getErgebnisoptionen('Erfolgreich', aufgabe);
  const { ergebnis, openModal, closeModal } = useErgebnisOptionenModal();

  const handleErgebnisErfasst = async (ergebnis: AufgabeErgebnis | null, ergebnisOption?: string, kommentar?: string) => {
    if (ergebnis === 'Zwischenergebnis' && onAufgabeZwischenergebnisErfassen !== undefined) {
      await onAufgabeZwischenergebnisErfassen(aufgabe.id, ergebnisOption ?? '', kommentar ?? '');
    }

    if (ergebnis === 'Erfolgreich' || ergebnis === 'Erfolglos') {
      await onAufgabeAbschliessen(aufgabe.id, ergebnis, ergebnisOption, kommentar);
    }

    closeModal();
  };

  const startUmfrage = async () => {
    const redirectUri = `capacitor://salesplanner.teambeverage.cloud/?token=${aufgabe.teilnehmerId}`;

    if (isNull(aufgabe.teilnehmerId)) {
      return;
    }

    const umfrageUri = new URL(`https://teambeverage.limesurvey.net/${aufgabe.umfrageId}`);
    umfrageUri.searchParams.set('token', aufgabe.teilnehmerId);
    umfrageUri.searchParams.set('newtest', 'Y');
    umfrageUri.searchParams.set('redirectUri', redirectUri);
    umfrageUri.searchParams.set('adressId', aufgabe.objekt.adressId.toString() ?? '');

    const event = await openAndWaitForCallback(umfrageUri.toString());
    Browser.close();
    console.log(`Return URL: ${event.url}`);
  };

  return (
    <>
      {(canAufgabeAbschliessen(aufgabe)) ? (
        <>
          <ButtonCtaPrimary icon={ChatCenteredDots} onClick={() => startUmfrage()}>Umfrage starten</ButtonCtaPrimary>
          <ButtonCtaTertiary icon={CheckCircle} onClick={() => handleErgebnisErfasst('Erfolgreich', erfolgreichErgebnisOption)}>{ergebnisLabelMapping.Erfolgreich}</ButtonCtaTertiary>
          {
            onAufgabeZwischenergebnisErfassen !== undefined ?
              <ButtonCtaGhost icon={ClipboardText} onClick={() => openModal('Zwischenergebnis')}>{ergebnisLabelMapping.Zwischenergebnis}</ButtonCtaGhost> :
              null
          }
          <ButtonCtaDanger icon={XCircle} onClick={() => openModal('Erfolglos')}>{ergebnisLabelMapping.Erfolglos}</ButtonCtaDanger>
        </>
      ) : null}
      {!isNull(ergebnis) ? (
        <ErgebnisOptionenModal
          aufgabe={aufgabe}
          ergebnis={ergebnis}
          onClose={closeModal}
          onErgebnisErfasst={(ergebnisOption, kommentar) => handleErgebnisErfasst(ergebnis, ergebnisOption, kommentar)} />
      ) : null}
    </>
  );
};
